import * as React from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import { visuallyHidden } from '@mui/utils';
import { useState } from 'react';
import { useEffect } from 'react';
import { useCallback } from 'react';
import { Chip, Container, Menu, MenuItem, Stack } from '@mui/material';
import useAxiosPrivate from '../../hooks/use-axios-private';
import Empty from '../../components/Empty';
import moment from 'moment';
import { useTheme } from '@emotion/react';
import { DateRange } from 'react-date-range';
import Badge from '@mui/material/Badge';
import { APP_CURRENCY, APP_DATE_FORMAT } from '../../constants';

const transactionStatusColor = (txnType) => {
    switch (txnType) {
        case "CREDIT":
            return 'success'
        case "DEBIT":
            return 'error'
        case "REFUND":
            return 'info'
        default:
            return 'info'
    }

}
function Row({ row, }) {
    // const { row } = props;
    return (

        <React.Fragment>
            <TableRow sx={{ height: { xs: 65, xl: 70 } }}>
                <TableCell sx={{ whiteSpace: "nowrap" }} align="center">{moment(row.created_on).format(APP_DATE_FORMAT)}</TableCell>
                <TableCell sx={{ whiteSpace: "pre-wrap", minWidth: "250px", maxWidth: "250px" }} align="center">
                    <Typography sx={{ fontSize: 14, fontWeight: '500' }}>{row.transaction_details.type}</Typography>
                    <Typography sx={{ fontSize: 14 }}>{row.transaction_details.message}</Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }} align="center">
                    <Chip color={transactionStatusColor(row.transaction_type_code)} label={row.transaction_type.toUpperCase()} variant="outlined" />
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }} align="center"><Typography sx={{ fontSize: 14 }} color={row.transaction_status ? "success.main" : "error.main"}> {row.transaction_status ? "Success" : "Failed"}</Typography></TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", }} align="center">{APP_CURRENCY.currency_symbol} {Number(row.amount).toFixed(2)}</TableCell>
            </TableRow>
        </React.Fragment>
    );
}


const headCells = [
    {
        id: 'created_on',
        numeric: true,
        disablePadding: false,
        label: 'Date',
    },
    {
        id: 'transaction_details',
        numeric: false,
        disablePadding: false,
        label: 'Transaction',
    },
    {
        id: 'transaction_type',
        numeric: true,
        disablePadding: false,
        label: 'Transaction Type',
    },
    {
        id: 'transaction_status',
        numeric: true,
        disablePadding: false,
        label: 'Status',
    },
    {
        id: 'amount',
        numeric: true,
        disablePadding: false,
        label: `Amount (${APP_CURRENCY.currency_symbol})`,
    },

];

const DEFAULT_ROWS_PER_PAGE = 5;

const EnhancedTableHead = (props) => {
    const { order, orderBy, onRequestSort } =
        props;

    const createSortHandler = (newOrderBy) => {

        onRequestSort(newOrderBy);
    };
    return (
        <TableHead>
            <TableRow sx={{ height: { xs: 65, lg: 70 } }}>
                {headCells.map((headCell, index) => (
                    <TableCell
                        key={index}
                        align={'center'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                        sx={{ whiteSpace: "nowrap" }}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy}


                            onClick={() => createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            )
                                : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    //  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    // orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

const EnhancedTableToolbar = (props) => {
    const theme = useTheme()
    const [showFilter, setShowFilter] = useState(false)
    const [toggleDatePicker, setToggleDatePicker] = useState(false)
    const { numSelected, filterChange } = props
    const [appliedFilter, setAppliedFilter] = useState("ALL")
    const [filterCondition, setFilterCondition] = useState(
        {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection'
        }
    )
    const onFilterChange = (e) => {
        if (e !== "CUSTOM") {
            setAppliedFilter(e)
        }
        if (e === "CUSTOM") {
            setToggleDatePicker(true)
            return
        }
        else if (e === "7DAYS") {
            const date = new Date()
            const currentDate = moment(date)
            const sevenDays = date.setDate(date.getDate() - 7)
            const last7Days = moment(sevenDays)
            const filterCondition = {
                startDate: moment.utc(last7Days).format(),
                endDate: moment.utc(currentDate).format(),
            }
            filterChange(filterCondition)
        }

        else if (e === "LST_30_DAYS") {
            const date = new Date()
            const currentDate = moment(date)
            const thirtyDays = date.setDate(date.getDate() - 30)
            const last30Days = moment(thirtyDays)
            const filterCondition = {
                startDate: moment.utc(last30Days).format(),
                endDate: moment.utc(currentDate).format(),
            }
            filterChange(filterCondition)
        }

        else if (e === "LST_MONTH") {
            const date = new Date()
            const currentYear = date.getFullYear()
            const month = date.setMonth(date.getMonth() - 1)
            const NumberOfDaysInMonth = new Date(month);
            const firstDay = new Date(currentYear, NumberOfDaysInMonth.getMonth(), 1);
            const lastDay = new Date(currentYear, NumberOfDaysInMonth.getMonth() + 1, 0);
            const filterCondition = {
                startDate: moment.utc(firstDay).format(),
                endDate: moment.utc(lastDay).format(),
            }
            filterChange(filterCondition)
        }

        else if (e === "LST_3_MONTH") {
            const date = new Date()
            const currentYear = date.getFullYear()
            const month = date.setMonth(date.getMonth() - 3)
            const NumberOfDaysInMonth = new Date(month);
            const firstDay = new Date(currentYear, NumberOfDaysInMonth.getMonth(), 1);
            const lastDay = new Date(currentYear, NumberOfDaysInMonth.getMonth() + 1, 0);
            const filterCondition = {
                startDate: moment.utc(firstDay).format(),
                endDate: moment.utc(lastDay).format(),
            }
            filterChange(filterCondition)
        }

        else if (e === "ALL") {
            const filterCondition = {
                startDate: '',
                endDate: '',
            }
            filterChange(filterCondition)
        }
        setShowFilter(false)
    }
    return (
        <>
            <Box sx={{ mt: 3 }}>
                <Toolbar
                    sx={{
                        px: { xs: 2 },
                        ...(numSelected > 0 && {
                            bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                        }),
                        alignItems: { xs: 'end', sm: 'center' }
                    }}
                >
                    {numSelected > 0 ? (
                        <Typography
                            sx={{ flex: '1 1 100%' }}
                            color="inherit"
                            variant="subtitle1"
                            component="div"
                        >
                            {numSelected} selected
                        </Typography>
                    ) : (
                        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'baseline', sm: 'center' }} justifyContent='space-between' sx={{ width: '100%', mt: { xs: 3, sm: 0 } }}>
                            <Typography
                                sx={{ flex: '1 1 100%', fontSize: { xs: 18, xl: 20 }, fontWeight: 500, color: theme.palette.mode === "dark" ? theme.palette.primary.contrastText : theme.palette.primary.main }}
                                id="tableTitle"
                                component="div"

                            >
                                Payment History
                            </Typography>
                            {/* <Box sx={{ mr: 3, width: { xs: '100%', sm: 300 }, maxWidth: '100%', mt: { xs: 1, sm: 0 } }}>
                                <TextField fullWidth label="Search" variant="standard" size="small" onChange={onSearchChange} />
                            </Box> */}
                        </Stack>
                    )}

                    {numSelected > 0 ? (
                        <Box sx={{ mr: 1 }}>
                            <Tooltip title="Delete">
                                <IconButton color='error'>
                                    <DeleteOutlineOutlinedIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Duplicate">
                                <IconButton>
                                    <ContentCopyOutlinedIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                                <IconButton>
                                    <EditOutlinedIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ) : (
                        <>
                            <Tooltip title="Filter">
                                <Badge overlap="circular" badgeContent=" " color="secondary" invisible={appliedFilter === 'ALL'} variant="dot">
                                    <IconButton sx={{ backgroundColor: '#f3f3f3' }} onClick={(e) => {
                                        setShowFilter(prev => {
                                            return prev ? false : e.target
                                        })
                                    }}>
                                        <FilterAltOutlinedIcon />
                                    </IconButton>
                                </Badge>
                            </Tooltip>

                            <Menu
                                open={showFilter}
                                anchorEl={showFilter}
                                onClose={() => { setShowFilter(false); setToggleDatePicker(false) }}
                            >
                                {toggleDatePicker ?
                                    <DateRange
                                        editableDateInputs={true}
                                        onChange={(item) => {
                                            setFilterCondition(item.selection)
                                            let nextDay = new Date(item.selection.endDate.getTime())
                                            nextDay.setHours(item.selection.endDate.getHours() + 24)
                                            let lastDate = moment.utc(item.selection.endDate).format()
                                            lastDate = moment(lastDate).set({ 'hour': 23, 'minute': 59, 'second': 59 })
                                            filterChange(
                                                {
                                                    startDate: moment.utc(item.selection.startDate).format(),
                                                    endDate: item.selection.startDate.getTime() === item.selection.endDate.getTime()
                                                        ?
                                                        moment.utc(nextDay).format()
                                                        :
                                                        moment.utc(lastDate).format()
                                                }
                                            )
                                            setAppliedFilter("CUSTOM")
                                        }}
                                        moveRangeOnFirstSelection={false}
                                        ranges={[filterCondition]}
                                    />
                                    :
                                    <Box>
                                        <MenuItem onClick={() => onFilterChange('ALL')} sx={appliedFilter === "ALL" ? { background: "#ebecf0", borderLeft: "3px solid #0b1345", fontWeight: 500, fontSize: '15px' } : { fontSize: '15px', borderLeft: "3px solid transparent" }} >All</MenuItem>
                                        <MenuItem onClick={() => onFilterChange('7DAYS')} sx={appliedFilter === "7DAYS" ? { background: "#ebecf0", borderLeft: "3px solid #0b1345", fontWeight: 500, fontSize: '15px' } : { fontSize: '15px', borderLeft: "3px solid transparent" }}>Last 7 days</MenuItem>
                                        <MenuItem onClick={() => onFilterChange('LST_30_DAYS')} sx={appliedFilter === "LST_30_DAYS" ? { background: "#ebecf0", borderLeft: "3px solid #0b1345", fontWeight: 500, fontSize: '15px' } : { fontSize: '15px', borderLeft: "3px solid transparent" }}>Last 30 days</MenuItem>
                                        <MenuItem onClick={() => onFilterChange('LST_MONTH')} sx={appliedFilter === "LST_MONTH" ? { background: "#ebecf0", borderLeft: "3px solid #0b1345", fontWeight: 500, fontSize: '15px' } : { fontSize: '15px', borderLeft: "3px solid transparent" }}>Last month</MenuItem>
                                        <MenuItem onClick={() => onFilterChange('LST_3_MONTH')} sx={appliedFilter === "LST_3_MONTH" ? { background: "#ebecf0", borderLeft: "3px solid #0b1345", fontWeight: 500, fontSize: '15px' } : { fontSize: '15px', borderLeft: "3px solid transparent" }}>Last 3 months</MenuItem>
                                        <MenuItem onClick={() => onFilterChange('CUSTOM')} sx={appliedFilter === "CUSTOM" ? { background: "#ebecf0", borderLeft: "3px solid #0b1345", fontWeight: 500, fontSize: '15px' } : { fontSize: '15px', borderLeft: "3px solid transparent" }}>Custom range</MenuItem>
                                    </Box>
                                }
                            </Menu>
                        </>
                    )}
                </Toolbar>
            </Box>
        </>
    );
}

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
};

const Transaction = ({ jobstatus }) => {
    const theme = useTheme()
    const [ascorder, setAscorder] = useState("desc")
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    const [paddingHeight, setPaddingHeight] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [count, setCount] = useState(0);
    const axios = useAxiosPrivate()

    const [orderByJobs, setOrderByJobs] = useState([])
    const [bill, setBill] = useState([])
    const [paymentInfo, setPaymentInfo] = useState({})

    const [filter, setFilter] = useState(
        {
            startDate: '',
            endDate: '',
            key: 'selection'
        }
    );

    const getTransactions = async () => {
        try {
            const resp = await axios.get('users/transactions/', {
                params: {
                    limit: rowsPerPage, search: searchText, page: page + 1, status: jobstatus
                    , order_by: orderByJobs, order_by_type: ascorder,
                    start_date: filter.startDate,
                    end_date: filter.endDate
                }
            })
            setBill(resp.data.data.wallet_transactions)
            setPaymentInfo(resp.data.data.page_info)
            setCount(resp.data.data.page_info.count)

        } catch (error) {

            console.error(error)
        }
    }
    useEffect(() => {
        getTransactions()
    }, [rowsPerPage, searchText, page, ascorder, orderByJobs, filter]);


    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            return;
        }
        setSelected([]);
    };
    const orderChange = (newOrderBy) => {
        if (ascorder === "desc") {
            setAscorder("asc")
            setOrderByJobs(newOrderBy)
        }
        else {
            setAscorder("desc")
            setOrderByJobs(newOrderBy)
        }
    }


    const handleChangePage = useCallback(
        (event, newPage) => {
            setPage(newPage)
        },
        []
    );

    const handleChangeRowsPerPage = useCallback(
        (event) => {
            const updatedRowsPerPage = parseInt(event.target.value, 10);
            setRowsPerPage(updatedRowsPerPage);
            setPage(0);
            // There is no layout jump to handle on the first page.
            setPaddingHeight(0);
        }, []
    );
    return (
        <Container maxWidth="xl">
            <Stack>
                <Box sx={{ mt: 3 }}>
                    <Typography sx={{ position: 'relative', pl: 1, fontSize: { xs: 20, md: 22, xl: 25 }, fontWeight: theme.palette.mode === "dark" ? '400' : '500', "&::after": { content: '""', position: 'absolute', background: '#f764a3', width: '3px', left: 0, top: '12.5%', bottom: '12.5%', borderRadius: 1 } }}>Transaction</Typography>
                </Box>
            </Stack>
            <Box sx={{ width: '100%' }}>
                <Paper sx={{ width: '100%', mb: 2, boxShadow: "2px 2px 12px 0px rgb(0 0 0 / 11%)" }}>
                    <Box>
                        <EnhancedTableToolbar
                            numSelected={selected.length}
                            onSearchChange={
                                (e) => { setSearchText(e.target.value) }
                            }
                            filterChange={
                                (e) => {
                                    setFilter(e)
                                }
                            }
                            filter={filter}
                        />

                        {bill.length > 0
                            ?
                            <>
                                <TableContainer>
                                    <Table
                                        sx={{ minWidth: 750, }}
                                        aria-labelledby="tableTitle"
                                        size={'small'}
                                    >
                                        <EnhancedTableHead
                                            numSelected={selected.length}
                                            //   order={order}
                                            //  orderBy={orderBy}
                                            onSelectAllClick={handleSelectAllClick}

                                            //  onRequestSort={orderChange}
                                            onRequestSort={(newOrderBy) => { orderChange(newOrderBy) }}
                                            rowCount={bill.length}
                                            orderBy={ascorder}
                                        />
                                        <TableBody>
                                            {bill
                                                && bill.map((row, index) => <Row key={row.id} row={row} paymentInfo={paymentInfo} />)
                                            }
                                            {paddingHeight > 0 && (
                                                <TableRow
                                                    style={{
                                                        height: paddingHeight,
                                                    }}
                                                >
                                                    <TableCell />
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25]}
                                    component="div"
                                    count={count}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </>
                            :
                            <Empty sx={{ border: "none", }} icon={<RequestQuoteOutlinedIcon sx={{ fontSize: 40, color: "grey" }} />} text="No transactions available" />
                        }
                    </Box>
                </Paper>
            </Box>
        </Container>

    );
}

export default Transaction